import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import RefreshIcon from "@mui/icons-material/Refresh";
import SendIcon from "@mui/icons-material/Send";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import {
  askClaudeAnalysisApi,
  cancelClaudeActionApi,
  claudeOpsChatApi,
  confirmClaudeActionApi,
  createClaudeChatApi,
  deleteClaudeChatApi,
  exportClaudeAudienceApi,
  fetchClaudeSnapshotApi,
  fetchClaudeStatusApi,
  getClaudeChatApi,
  listClaudeChatsApi,
} from "../../services/claudeAnalysis";
import { toastMessage } from "../../utils/toastMessage";

const formatIstDatetimeLocal = (input) => {
  if (!input) return "";
  const raw = String(input).trim();
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(raw) && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(raw)) {
    return raw.slice(0, 16);
  }
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "";
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Kolkata",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    })
      .formatToParts(date)
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, part.value])
  );
  const hour = parts.hour === "24" ? "00" : parts.hour;
  return `${parts.year}-${parts.month}-${parts.day}T${hour}:${parts.minute}`;
};

const toIstIsoFromDatetimeLocal = (value) => {
  const cleaned = String(value || "").trim();
  if (!cleaned) return "";
  if (/[zZ]|[+-]\d{2}:?\d{2}$/.test(cleaned)) return cleaned;
  const withSeconds = cleaned.length === 16 ? `${cleaned}:00` : cleaned;
  return `${withSeconds}+05:30`;
};

const emptyConfirmDraft = () => ({
  mode: "send_now",
  title: "",
  message: "",
  titleHi: "",
  messageHi: "",
  scheduledAt: "",
});

// Match admin panel brand (same as Notification / User Management)
const ADMIN_THEME = {
  primary: "#ff8989",
  primaryHover: "#ff6b6b",
  soft: "#fff0f0",
  softBorder: "#ffc9c9",
  selectedBg: "#ffe8e8",
  text: "#333333",
};

const adminBtnSx = {
  bgcolor: ADMIN_THEME.primary,
  color: "#fff",
  boxShadow: "none",
  "&:hover": { bgcolor: ADMIN_THEME.primaryHover, boxShadow: "none" },
  "&.Mui-disabled": { bgcolor: "#ffc9c9", color: "#fff" },
};

const adminOutlinedBtnSx = {
  borderColor: ADMIN_THEME.primary,
  color: ADMIN_THEME.primary,
  "&:hover": {
    borderColor: ADMIN_THEME.primaryHover,
    bgcolor: ADMIN_THEME.soft,
  },
};

const WELCOME_MESSAGE =
  'Ops chat ready (English / Hinglish only).\n\n1) Pehle audience lao — e.g. "2 consultation complete users ki list do"\n2) Phir unpe task — e.g. "inke pass raat 12 baje ye notification schedule kar do"\n\nSend/schedule Confirm ke baad hi hoga.\nList download: Current Audience panel se Download CSV.';

const DAY_OPTIONS = [
  { value: 1, label: "Last 1 day" },
  { value: 7, label: "Last 7 days" },
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
];

const FALLBACK_CATALOG = [
  {
    id: "overview",
    label: "Overview",
    description: "Cross-module ops health.",
    subsections: [
      {
        id: "ops-health",
        label: "Ops Health",
        description: "Top KPIs for the selected window.",
        questions: ["What should operations act on first from this window?"],
      },
    ],
  },
];

const CHAT_SUGGESTIONS = [
  "2 consultation complete wale users ki list do (name ke saath)",
  "72 hours se consultation nahi li un users ka data do",
  "Recharge kiya lekin consultation nahi li — unki list do",
  "In users pe notification schedule karo aaj raat 12 baje, title: Come back, message: Your favourite astrologer is online",
];

const emptyChatContext = () => ({
  audienceUserIds: [],
  audienceTotal: 0,
  audienceFilters: null,
  audiencePreview: [],
  pendingAction: null,
});

const humanize = (key = "") =>
  String(key)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const formatMetricValue = (value) => {
  if (value == null || value === "") return "—";
  if (typeof value === "number") {
    if (Number.isInteger(value)) return value.toLocaleString();
    return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  return String(value);
};

const isLeafMetric = (value) =>
  value != null &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  ("count" in value || "amountInr" in value || "avg" in value || "sum" in value);

const isFlatObject = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  return Object.values(value).every(
    (item) => item == null || ["string", "number", "boolean"].includes(typeof item)
  );
};

const isStatusMap = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const entries = Object.values(value);
  return entries.length > 0 && entries.every(isLeafMetric);
};

const StatCard = ({ title, value, subtitle }) => (
  <Card sx={{ height: "100%" }}>
    <CardContent>
      <Typography color="textSecondary" gutterBottom variant="body2">{title}</Typography>
      <Typography variant="h4" component="h2" sx={{ fontWeight: "bold" }}>
        {formatMetricValue(value)}
      </Typography>
      {subtitle ? (
        <Typography variant="caption" color="textSecondary">{subtitle}</Typography>
      ) : null}
    </CardContent>
  </Card>
);

const FlatMetricsGrid = ({ title, data }) => {
  const entries = Object.entries(data || {});
  if (!entries.length) return null;
  return (
    <Box sx={{ mb: 2 }}>
      {title ? <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>{title}</Typography> : null}
      <Grid container spacing={1.5}>
        {entries.map(([key, value]) => (
          <Grid item xs={6} sm={4} md={3} key={key}>
            <Card variant="outlined">
              <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                <Typography variant="caption" color="textSecondary">{humanize(key)}</Typography>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>{formatMetricValue(value)}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

const StatusTable = ({ title, data }) => {
  const rows = Object.entries(data || {});
  if (!rows.length) return null;
  const showAmount = rows.some(([, row]) => row?.amountInr != null && row.amountInr !== 0);
  const showAvg = rows.some(([, row]) => row?.avg != null);
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>{title}</Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>Name</TableCell>
              <TableCell align="right" sx={{ fontWeight: "bold" }}>Count</TableCell>
              {showAmount ? <TableCell align="right" sx={{ fontWeight: "bold" }}>Amount (INR)</TableCell> : null}
              {showAvg ? <TableCell align="right" sx={{ fontWeight: "bold" }}>Avg</TableCell> : null}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(([key, row]) => (
              <TableRow key={key}>
                <TableCell>{humanize(key)}</TableCell>
                <TableCell align="right">{formatMetricValue(row?.count)}</TableCell>
                {showAmount ? <TableCell align="right">{formatMetricValue(row?.amountInr)}</TableCell> : null}
                {showAvg ? <TableCell align="right">{formatMetricValue(row?.avg)}</TableCell> : null}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const MetricsPanels = ({ metrics }) => {
  if (!metrics || typeof metrics !== "object") {
    return <Typography color="textSecondary">No metrics loaded yet.</Typography>;
  }
  return (
    <Box>
      {Object.entries(metrics).map(([groupKey, groupValue]) => {
        if (groupValue == null) return null;
        if (typeof groupValue !== "object" || Array.isArray(groupValue)) {
          return (
            <Box key={groupKey} sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="textSecondary">{humanize(groupKey)}</Typography>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>{formatMetricValue(groupValue)}</Typography>
            </Box>
          );
        }
        if (isStatusMap(groupValue)) return <StatusTable key={groupKey} title={humanize(groupKey)} data={groupValue} />;
        if (isFlatObject(groupValue)) return <FlatMetricsGrid key={groupKey} title={humanize(groupKey)} data={groupValue} />;
        return (
          <Box key={groupKey} sx={{ mb: 2.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>{humanize(groupKey)}</Typography>
            {Object.entries(groupValue).map(([childKey, childValue]) => {
              if (childValue == null) return null;
              if (typeof childValue !== "object" || Array.isArray(childValue)) {
                return (
                  <Box key={childKey} sx={{ mb: 1 }}>
                    <Typography variant="caption" color="textSecondary">{humanize(childKey)}</Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>{formatMetricValue(childValue)}</Typography>
                  </Box>
                );
              }
              if (isStatusMap(childValue)) return <StatusTable key={childKey} title={humanize(childKey)} data={childValue} />;
              if (isFlatObject(childValue)) return <FlatMetricsGrid key={childKey} title={humanize(childKey)} data={childValue} />;
              if (isLeafMetric(childValue)) {
                return (
                  <Box key={childKey} sx={{ mb: 1 }}>
                    <Typography variant="caption" color="textSecondary">{humanize(childKey)}</Typography>
                    <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                      Count: {formatMetricValue(childValue.count)}
                      {childValue.amountInr != null ? ` · ₹${formatMetricValue(childValue.amountInr)}` : ""}
                    </Typography>
                  </Box>
                );
              }
              return <FlatMetricsGrid key={childKey} title={humanize(childKey)} data={childValue} />;
            })}
          </Box>
        );
      })}
    </Box>
  );
};

const ClaudeAnalysis = () => {
  const [mainTab, setMainTab] = useState(0);
  const [days, setDays] = useState(7);
  const [catalog, setCatalog] = useState(FALLBACK_CATALOG);
  const [sectionId, setSectionId] = useState("overview");
  const [subsectionId, setSubsectionId] = useState("ops-health");
  const [question, setQuestion] = useState("");
  const [status, setStatus] = useState({ configured: false, model: "" });
  const [snapshot, setSnapshot] = useState(null);
  const [answer, setAnswer] = useState("");
  const [history, setHistory] = useState([]);
  const [loadingSnapshot, setLoadingSnapshot] = useState(false);
  const [asking, setAsking] = useState(false);

  // Ops chat state
  const [chatInput, setChatInput] = useState("");
  const [chatMessages, setChatMessages] = useState([
    { role: "assistant", content: WELCOME_MESSAGE },
  ]);
  const [chatContext, setChatContext] = useState(emptyChatContext());
  const [chatLoading, setChatLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmDraft, setConfirmDraft] = useState(emptyConfirmDraft());
  const [confirmDraftError, setConfirmDraftError] = useState({});
  const [exportingAudience, setExportingAudience] = useState(false);
  const [chatSessions, setChatSessions] = useState([]);
  const [activeChatId, setActiveChatId] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const chatEndRef = useRef(null);

  const selectedSection = useMemo(
    () => catalog.find((item) => item.id === sectionId) || catalog[0],
    [catalog, sectionId]
  );
  const subsections = selectedSection?.subsections || [];
  const selectedSubsection = useMemo(
    () => subsections.find((item) => item.id === subsectionId) || subsections[0],
    [subsections, subsectionId]
  );
  const suggestedQuestions = selectedSubsection?.questions || [];

  const resetConversation = () => {
    setHistory([]);
    setAnswer("");
  };

  const loadSnapshot = async (windowDays, section, subsection) => {
    try {
      setLoadingSnapshot(true);
      const { data } = await fetchClaudeSnapshotApi({ days: windowDays, section, subsection });
      if (data?.success) setSnapshot(data.result);
      else toastMessage(data?.message || "Could not load snapshot", "error");
    } catch (error) {
      toastMessage(error?.response?.data?.message || error?.message || "Could not load snapshot", "error");
    } finally {
      setLoadingSnapshot(false);
    }
  };

  const refreshChatHistory = async () => {
    try {
      setHistoryLoading(true);
      const { data } = await listClaudeChatsApi();
      if (data?.success) setChatSessions(data.result || []);
    } catch (error) {
      // keep silent on first load; page still usable
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    const loadStatus = async () => {
      try {
        const { data } = await fetchClaudeStatusApi();
        if (data?.success) {
          setStatus({
            configured: Boolean(data.result?.configured),
            model: data.result?.model || "",
          });
          if (Array.isArray(data.result?.catalog) && data.result.catalog.length) {
            setCatalog(data.result.catalog);
            const first = data.result.catalog[0];
            setSectionId(first.id);
            setSubsectionId(first.subsections?.[0]?.id || "");
            setQuestion(first.subsections?.[0]?.questions?.[0] || "");
          }
        }
      } catch (error) {
        setStatus({ configured: false, model: "" });
      }
    };
    loadStatus();
    refreshChatHistory();
  }, []);

  useEffect(() => {
    if (mainTab !== 1) return;
    if (!sectionId || !subsectionId) return;
    loadSnapshot(days, sectionId, subsectionId);
  }, [mainTab, days, sectionId, subsectionId]);

  useEffect(() => {
    if (selectedSubsection?.questions?.[0]) setQuestion(selectedSubsection.questions[0]);
  }, [selectedSubsection?.id]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages, chatLoading, chatContext.pendingAction]);

  const handleSectionChange = (nextSectionId) => {
    const next = catalog.find((item) => item.id === nextSectionId) || catalog[0];
    setSectionId(next.id);
    setSubsectionId(next.subsections?.[0]?.id || "");
    resetConversation();
  };

  const handleAskMetrics = async () => {
    const trimmed = question.trim();
    if (!trimmed) {
      toastMessage("Enter a question for Claude.", "error");
      return;
    }
    try {
      setAsking(true);
      const { data } = await askClaudeAnalysisApi({
        question: trimmed,
        days,
        history,
        section: sectionId,
        subsection: subsectionId,
      });
      if (!data?.success) {
        toastMessage(data?.message || "Claude could not answer", "error");
        return;
      }
      const nextAnswer = data.result?.answer || "";
      setAnswer(nextAnswer);
      if (data.result?.snapshot) setSnapshot(data.result.snapshot);
      setHistory((prev) => [...prev, { role: "user", content: trimmed }, { role: "assistant", content: nextAnswer }].slice(-6));
    } catch (error) {
      toastMessage(error?.response?.data?.message || error?.message || "Claude request failed", "error");
    } finally {
      setAsking(false);
    }
  };

  const applySessionToUi = (session) => {
    const messages = Array.isArray(session?.messages) && session.messages.length
      ? session.messages.map((m) => ({ role: m.role, content: m.content }))
      : [{ role: "assistant", content: WELCOME_MESSAGE }];
    const context = {
      ...emptyChatContext(),
      ...(session?.context || {}),
    };
    const pending = context.pendingAction;
    if (
      pending?.expiresAt &&
      new Date(pending.expiresAt).getTime() < Date.now()
    ) {
      context.pendingAction = null;
    }
    setActiveChatId(session?._id || null);
    setChatMessages(messages);
    setChatContext(context);
    setChatInput("");
  };

  const handleNewChat = async () => {
    try {
      const { data } = await createClaudeChatApi();
      if (!data?.success) {
        toastMessage(data?.message || "Could not create chat", "error");
        return;
      }
      applySessionToUi(data.result);
      await refreshChatHistory();
    } catch (error) {
      toastMessage(error?.response?.data?.message || error?.message || "Could not create chat", "error");
    }
  };

  const handleOpenChat = async (id) => {
    if (!id || id === activeChatId) return;
    try {
      setHistoryLoading(true);
      const { data } = await getClaudeChatApi(id);
      if (!data?.success) {
        toastMessage(data?.message || "Could not open chat", "error");
        return;
      }
      applySessionToUi(data.result);
    } catch (error) {
      toastMessage(error?.response?.data?.message || error?.message || "Could not open chat", "error");
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleDeleteChat = async (id, event) => {
    event?.stopPropagation?.();
    try {
      const { data } = await deleteClaudeChatApi(id);
      if (!data?.success) {
        toastMessage(data?.message || "Could not delete chat", "error");
        return;
      }
      if (String(activeChatId) === String(id)) {
        setActiveChatId(null);
        setChatMessages([{ role: "assistant", content: WELCOME_MESSAGE }]);
        setChatContext(emptyChatContext());
      }
      await refreshChatHistory();
    } catch (error) {
      toastMessage(error?.response?.data?.message || error?.message || "Could not delete chat", "error");
    }
  };

  const handleChatSend = async (overrideText) => {
    const trimmed = String(overrideText ?? chatInput).trim();
    if (!trimmed) {
      toastMessage("Enter a prompt.", "error");
      return;
    }
    if (!status.configured) {
      toastMessage("Claude API key missing in _constant.js", "error");
      return;
    }

    const historyForApi = chatMessages
      .filter((m) => m.role === "user" || m.role === "assistant")
      .map((m) => ({ role: m.role, content: m.content }))
      .slice(-12);

    setChatMessages((prev) => [...prev, { role: "user", content: trimmed }]);
    setChatInput("");
    setChatLoading(true);

    try {
      const { data } = await claudeOpsChatApi({
        message: trimmed,
        history: historyForApi,
        context: chatContext,
        sessionId: activeChatId,
      });
      if (!data?.success) {
        toastMessage(data?.message || "Chat failed", "error");
        setChatMessages((prev) => [
          ...prev,
          { role: "assistant", content: data?.message || "Chat failed. Try again." },
        ]);
        return;
      }

      const result = data.result || {};
      if (result.context) setChatContext(result.context);
      if (result.sessionId) setActiveChatId(result.sessionId);
      setChatMessages((prev) => [
        ...prev,
        { role: "assistant", content: result.reply || "Done." },
      ]);
      refreshChatHistory();
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || "Chat failed";
      toastMessage(message, "error");
      setChatMessages((prev) => [...prev, { role: "assistant", content: message }]);
    } finally {
      setChatLoading(false);
    }
  };

  const openConfirmModal = () => {
    const pending = chatContext.pendingAction;
    if (!pending) return;

    if (!activeChatId) {
      toastMessage("Chat session missing. New chat banao / pehle prompt bhejo, phir confirm.", "error");
      return;
    }

    if (pending.expiresAt && new Date(pending.expiresAt).getTime() < Date.now()) {
      toastMessage("Pending action expired. Prepare notification again.", "error");
      setChatContext((prev) => ({ ...prev, pendingAction: null }));
      return;
    }

    if (!pending.confirmToken) {
      toastMessage("Invalid pending action. Prepare notification again.", "error");
      setChatContext((prev) => ({ ...prev, pendingAction: null }));
      return;
    }

    const isSchedule = pending.type === "schedule_notification";
    setConfirmDraft({
      mode: isSchedule ? "schedule" : "send_now",
      title: pending.title || "",
      message: pending.message || "",
      titleHi: pending.titleHi || "",
      messageHi: pending.messageHi || "",
      scheduledAt: formatIstDatetimeLocal(pending.scheduledAt) || "",
    });
    setConfirmDraftError({});
    setConfirmModalOpen(true);
  };

  const closeConfirmModal = () => {
    if (confirming) return;
    setConfirmModalOpen(false);
    setConfirmDraftError({});
  };

  const validateConfirmDraft = (draft) => {
    const errors = {};
    const title = String(draft.title || "").trim();
    const message = String(draft.message || "").trim();
    if (!title) errors.title = "Title required";
    else if (title.length > 120) errors.title = "Title max 120 chars";
    if (!message) errors.message = "Message required";
    else if (message.length > 500) errors.message = "Message max 500 chars";

    if (draft.mode === "schedule") {
      if (!draft.scheduledAt) {
        errors.scheduledAt = "Schedule time required";
      } else {
        const when = new Date(toIstIsoFromDatetimeLocal(draft.scheduledAt));
        if (Number.isNaN(when.getTime()) || when <= new Date()) {
          errors.scheduledAt = "Schedule time must be in the future (IST)";
        }
      }
    }
    return errors;
  };

  const handleConfirmAction = async () => {
    const pending = chatContext.pendingAction;
    if (!pending) return;
    if (!activeChatId) {
      toastMessage("Chat session missing. Confirm ke liye active chat zaroori hai.", "error");
      return;
    }

    const errors = validateConfirmDraft(confirmDraft);
    setConfirmDraftError(errors);
    if (Object.keys(errors).length) {
      toastMessage("Confirm se pehle title / message / time check karo.", "error");
      return;
    }

    const title = String(confirmDraft.title || "").trim();
    const message = String(confirmDraft.message || "").trim();
    const isSchedule = confirmDraft.mode === "schedule";
    const scheduledAt = isSchedule
      ? toIstIsoFromDatetimeLocal(confirmDraft.scheduledAt)
      : null;

    const pendingAction = {
      ...pending,
      type: isSchedule ? "schedule_notification" : "send_notification",
      title,
      message,
      titleHi: String(confirmDraft.titleHi || "").trim().slice(0, 120),
      messageHi: String(confirmDraft.messageHi || "").trim().slice(0, 500),
      scheduledAt,
      source: "claude_ops_chat",
    };

    try {
      setConfirming(true);
      const { data } = await confirmClaudeActionApi({
        confirmed: true,
        pendingAction,
        sessionId: activeChatId,
      });
      if (!data?.success) {
        toastMessage(data?.message || "Confirm failed", "error");
        return;
      }
      const summary = data.result?.summary || data.message || "Action completed";
      toastMessage(summary, "success");
      setConfirmModalOpen(false);
      setConfirmDraft(emptyConfirmDraft());
      setChatContext((prev) => ({ ...prev, pendingAction: null }));
      setChatMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `Confirmed.\n\nTitle: ${title}\nMessage: ${message}${
            scheduledAt ? `\nWhen: ${scheduledAt}` : ""
          }\n\n${summary}`,
        },
      ]);
      refreshChatHistory();
    } catch (error) {
      toastMessage(error?.response?.data?.message || error?.message || "Confirm failed", "error");
    } finally {
      setConfirming(false);
    }
  };

  const handleCancelPending = async () => {
    setConfirmModalOpen(false);
    setConfirmDraft(emptyConfirmDraft());
    setConfirmDraftError({});
    setChatContext((prev) => ({ ...prev, pendingAction: null }));
    setChatMessages((prev) => [
      ...prev,
      { role: "assistant", content: "Pending notification cancelled. MongoDB write nahi hua." },
    ]);
    if (!activeChatId) return;
    try {
      await cancelClaudeActionApi({ sessionId: activeChatId });
      refreshChatHistory();
    } catch (error) {
      // Local cancel already applied; session sync failure is non-blocking.
      console.warn("cancel-action sync failed", error);
    }
  };

  const handleDownloadAudience = async () => {
    if (!chatContext.audienceFilters || !Object.keys(chatContext.audienceFilters).length) {
      toastMessage("Pehle audience list fetch karo, phir download.", "error");
      return;
    }
    try {
      setExportingAudience(true);
      const response = await exportClaudeAudienceApi({
        filters: chatContext.audienceFilters,
      });
      const contentType = String(response?.headers?.["content-type"] || "");
      if (contentType.includes("application/json")) {
        const text = await response.data.text();
        const parsed = JSON.parse(text || "{}");
        toastMessage(parsed?.message || "Export failed", "error");
        return;
      }

      const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      link.href = url;
      link.download = `claude-audience-${stamp}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toastMessage(
        `Audience CSV downloaded (${(chatContext.audienceTotal || 0).toLocaleString()} matched).`,
        "success"
      );
    } catch (error) {
      let message = error?.message || "Download failed";
      try {
        if (error?.response?.data instanceof Blob) {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text || "{}");
          if (parsed?.message) message = parsed.message;
        } else if (error?.response?.data?.message) {
          message = error.response.data.message;
        }
      } catch (_) {
        // keep fallback message
      }
      toastMessage(message, "error");
    } finally {
      setExportingAudience(false);
    }
  };

  const analyzing = snapshot?.analyzing || {
    sectionLabel: selectedSection?.label,
    subsectionLabel: selectedSubsection?.label,
    focus: selectedSubsection?.description,
  };
  const cards = snapshot?.highlightCards || [];
  const pending = chatContext.pendingAction;

  return (
    <div className="WrapperArea">
      <div className="WrapperBox">
        <Box sx={{ mb: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontWeight: "bold", color: ADMIN_THEME.primary }}>
                Claude Data Analysis
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                Ops Chat: pehle data/list lao, phir follow-up se notification task confirm karke chalao.
              </Typography>
            </Box>
            <Chip
              icon={<AutoAwesomeIcon />}
              label={status.configured ? `Connected · ${status.model}` : "API key missing"}
              color={status.configured ? "success" : "warning"}
              size="small"
            />
          </Box>
        </Box>

        <Tabs
          value={mainTab}
          onChange={(_, value) => setMainTab(value)}
          sx={{
            mb: 2,
            borderBottom: "1px solid #e0e0e0",
            "& .MuiTab-root": { textTransform: "none", fontWeight: 600 },
            "& .Mui-selected": { color: `${ADMIN_THEME.primary} !important` },
            "& .MuiTabs-indicator": { backgroundColor: ADMIN_THEME.primary },
          }}
        >
          <Tab label="Ops Chat (Data + Tasks)" />
          <Tab label="Metrics Analysis" />
        </Tabs>

        {mainTab === 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleNewChat}
                    sx={{ ...adminBtnSx, mb: 2 }}
                  >
                    New chat
                  </Button>
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
                    Chat history
                  </Typography>
                  {historyLoading && !chatSessions.length ? (
                    <Box display="flex" justifyContent="center" py={2}><CircularProgress size={20} /></Box>
                  ) : null}
                  <Box sx={{ maxHeight: 560, overflow: "auto" }}>
                    {!chatSessions.length && !historyLoading ? (
                      <Typography variant="body2" color="textSecondary">
                        No saved chats yet. Send a prompt to create history.
                      </Typography>
                    ) : null}
                    {chatSessions.map((session) => {
                      const selected = String(session._id) === String(activeChatId);
                      return (
                        <Box
                          key={session._id}
                          onClick={() => handleOpenChat(session._id)}
                          sx={{
                            p: 1.25,
                            mb: 1,
                            borderRadius: 1,
                            cursor: "pointer",
                            border: selected ? `1px solid ${ADMIN_THEME.primary}` : "1px solid #e0e0e0",
                            bgcolor: selected ? ADMIN_THEME.selectedBg : "#fff",
                            "&:hover": { bgcolor: selected ? ADMIN_THEME.selectedBg : "#fafafa" },
                          }}
                        >
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: "bold",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {session.title || "New chat"}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {session.updatedAt
                                  ? new Date(session.updatedAt).toLocaleString()
                                  : ""}
                              </Typography>
                            </Box>
                            <DeleteOutlineIcon
                              fontSize="small"
                              onClick={(event) => handleDeleteChat(session._id, event)}
                              sx={{ color: "#9e9e9e", "&:hover": { color: "#d32f2f" } }}
                            />
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={5}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} gap={1} flexWrap="wrap">
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                      {chatSessions.find((s) => String(s._id) === String(activeChatId))?.title || "Chat"}
                    </Typography>
                    <Button size="small" onClick={handleNewChat} sx={{ color: ADMIN_THEME.primary }}>
                      New chat
                    </Button>
                  </Box>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Jaise ChatGPT: pehle audience fetch, phir unpe notification send/schedule. History left side pe save hoti hai.
                  </Typography>

                  <Box
                    sx={{
                      height: 420,
                      overflow: "auto",
                      bgcolor: "#fafafa",
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                      p: 2,
                      mb: 2,
                    }}
                  >
                    {chatMessages.map((msg, index) => (
                      <Box
                        key={`${msg.role}-${index}`}
                        sx={{
                          mb: 1.5,
                          display: "flex",
                          justifyContent: msg.role === "user" ? "flex-end" : "flex-start",
                        }}
                      >
                        <Box
                          sx={{
                            maxWidth: "90%",
                            p: 1.5,
                            borderRadius: 1,
                            bgcolor: msg.role === "user" ? ADMIN_THEME.soft : "#fff",
                            border: msg.role === "user"
                              ? `1px solid ${ADMIN_THEME.softBorder}`
                              : "1px solid #e0e0e0",
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          <Typography variant="caption" color="textSecondary" sx={{ fontWeight: "bold" }}>
                            {msg.role === "user" ? "You" : "Claude"}
                          </Typography>
                          <Typography variant="body2" component="div" sx={{ mt: 0.5 }}>
                            {msg.content}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                    {chatLoading && (
                      <Box display="flex" alignItems="center" gap={1}>
                        <CircularProgress size={16} />
                        <Typography variant="caption" color="textSecondary">Working...</Typography>
                      </Box>
                    )}
                    <div ref={chatEndRef} />
                  </Box>

                  <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                    {CHAT_SUGGESTIONS.map((item) => (
                      <Chip
                        key={item}
                        size="small"
                        variant="outlined"
                        label={item}
                        onClick={() => setChatInput(item)}
                        sx={{
                          maxWidth: "100%",
                          borderColor: ADMIN_THEME.softBorder,
                          color: ADMIN_THEME.text,
                          "&:hover": { bgcolor: ADMIN_THEME.soft, borderColor: ADMIN_THEME.primary },
                        }}
                      />
                    ))}
                  </Box>

                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    placeholder="Prompt likho… e.g. 2 consultation complete users ki list do"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleChatSend();
                      }
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: ADMIN_THEME.primary,
                      },
                    }}
                  />
                  <Box display="flex" justifyContent="flex-end" mt={2}>
                    <Button
                      variant="contained"
                      onClick={() => handleChatSend()}
                      disabled={chatLoading || !status.configured}
                      startIcon={chatLoading ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                      sx={adminBtnSx}
                    >
                      {chatLoading ? "Working..." : "Send"}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1} flexWrap="wrap" mb={0.5}>
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                      Current Audience
                    </Typography>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={
                        exportingAudience
                          ? <CircularProgress size={14} color="inherit" />
                          : <DownloadIcon fontSize="small" />
                      }
                      onClick={handleDownloadAudience}
                      disabled={
                        exportingAudience ||
                        !chatContext.audienceFilters ||
                        !(chatContext.audienceTotal > 0)
                      }
                      sx={{ ...adminBtnSx, textTransform: "none" }}
                    >
                      {exportingAudience ? "Downloading..." : "Download CSV"}
                    </Button>
                  </Box>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    Follow-up notification isi audience pe lagegi. Download full matched list (User ID, name, phone, consults).
                  </Typography>
                  <Chip
                    size="small"
                    label={`Total matched: ${(chatContext.audienceTotal || 0).toLocaleString()}`}
                    sx={{
                      mb: 1,
                      bgcolor: ADMIN_THEME.soft,
                      color: ADMIN_THEME.primaryHover,
                      fontWeight: 600,
                      border: `1px solid ${ADMIN_THEME.softBorder}`,
                    }}
                  />
                  {chatContext.audienceFilters ? (
                    <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>
                      Filters: {JSON.stringify(chatContext.audienceFilters)}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      Abhi koi audience loaded nahi. Pehle list fetch karo.
                    </Typography>
                  )}

                  {chatContext.audiencePreview?.length > 0 && (
                    <TableContainer sx={{ maxHeight: 280, mt: 1 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: "bold", bgcolor: ADMIN_THEME.soft }}>Name</TableCell>
                            <TableCell sx={{ fontWeight: "bold", bgcolor: ADMIN_THEME.soft }}>Phone</TableCell>
                            <TableCell align="right" sx={{ fontWeight: "bold", bgcolor: ADMIN_THEME.soft }}>Consults</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {chatContext.audiencePreview.map((user) => (
                            <TableRow key={user.id}>
                              <TableCell>{user.name}</TableCell>
                              <TableCell>{user.phone || "—"}</TableCell>
                              <TableCell align="right">{user.completedSessionCount ?? "—"}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                  {chatContext.audienceTotal > (chatContext.audiencePreview?.length || 0) ? (
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: "block" }}>
                      Preview first {chatContext.audiencePreview.length} users. Download CSV se full {(chatContext.audienceTotal || 0).toLocaleString()} list milegi.
                    </Typography>
                  ) : null}
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                    Pending Task
                  </Typography>
                  {!pending ? (
                    <Typography variant="body2" color="textSecondary">
                      Jab notification prepare hogi, yahan Confirm dikhega. Bina Confirm ke MongoDB write / send / schedule nahi hoga.
                    </Typography>
                  ) : (
                    <Box>
                      <Chip
                        label="Mongo write pending — Confirm required"
                        color="warning"
                        size="small"
                        sx={{ mb: 1.5 }}
                      />
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Source:</strong> Claude Ops Chat
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Type:</strong> {pending.type === "schedule_notification" ? "Schedule" : "Send now"}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Recipients:</strong> {(pending.recipientCount || 0).toLocaleString()}
                      </Typography>
                      {pending.audienceFilters ? (
                        <Typography variant="caption" color="textSecondary" sx={{ display: "block", mb: 1 }}>
                          Filters: {JSON.stringify(pending.audienceFilters)}
                        </Typography>
                      ) : null}
                      {pending.scheduledAt ? (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>When:</strong> {pending.scheduledAt}
                        </Typography>
                      ) : null}
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Title:</strong> {pending.title}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        <strong>Message:</strong> {pending.message}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" sx={{ display: "block", mb: 1.5 }}>
                        Confirm pe modal khulega — title / message / time edit karke final Mongo write hoga.
                      </Typography>
                      <Box display="flex" gap={1} flexWrap="wrap">
                        <Button
                          variant="contained"
                          disabled={confirming}
                          onClick={openConfirmModal}
                          sx={adminBtnSx}
                        >
                          Confirm & Run
                        </Button>
                        <Button
                          variant="outlined"
                          disabled={confirming}
                          onClick={handleCancelPending}
                          sx={adminOutlinedBtnSx}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {mainTab === 1 && (
          <>
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Section"
                      value={sectionId}
                      onChange={(event) => handleSectionChange(event.target.value)}
                    >
                      {catalog.map((option) => (
                        <MenuItem key={option.id} value={option.id}>{option.label}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Sub-section"
                      value={subsectionId}
                      onChange={(event) => {
                        setSubsectionId(event.target.value);
                        resetConversation();
                      }}
                    >
                      {subsections.map((option) => (
                        <MenuItem key={option.id} value={option.id}>{option.label}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Window"
                      value={days}
                      onChange={(event) => {
                        setDays(Number(event.target.value));
                        resetConversation();
                      }}
                    >
                      {DAY_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={loadingSnapshot ? <CircularProgress size={16} sx={{ color: ADMIN_THEME.primary }} /> : <RefreshIcon />}
                      onClick={() => loadSnapshot(days, sectionId, subsectionId)}
                      disabled={loadingSnapshot}
                      sx={adminOutlinedBtnSx}
                    >
                      Refresh numbers
                    </Button>
                  </Grid>
                </Grid>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    {analyzing.sectionLabel || selectedSection?.label} → {analyzing.subsectionLabel || selectedSubsection?.label}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                    {analyzing.focus || selectedSubsection?.description}
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>Main Data</Typography>
                {loadingSnapshot ? (
                  <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
                ) : cards.length === 0 ? (
                  <Typography color="textSecondary">No highlight data for this scope yet.</Typography>
                ) : (
                  <Grid container spacing={2}>
                    {cards.map((card, index) => (
                      <Grid item xs={12} sm={6} md={3} key={card.key || index}>
                        <StatCard title={card.label} value={card.value} subtitle={card.subtitle} />
                      </Grid>
                    ))}
                  </Grid>
                )}
              </CardContent>
            </Card>

            <Grid container spacing={2}>
              <Grid item xs={12} lg={6}>
                <Card sx={{ height: "100%" }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>Detailed Breakdown</Typography>
                    <Divider sx={{ mb: 2 }} />
                    {loadingSnapshot ? (
                      <Box display="flex" justifyContent="center" py={6}><CircularProgress size={28} /></Box>
                    ) : (
                      <Box sx={{ maxHeight: 520, overflow: "auto", pr: 1 }}>
                        <MetricsPanels metrics={snapshot?.metrics} />
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Card sx={{ height: "100%" }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>Ask Claude (Metrics)</Typography>
                    <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                      {suggestedQuestions.map((item) => (
                        <Chip
                          key={item}
                          label={item}
                          size="small"
                          onClick={() => setQuestion(item)}
                          variant={question === item ? "filled" : "outlined"}
                          sx={
                            question === item
                              ? {
                                  bgcolor: ADMIN_THEME.primary,
                                  color: "#fff",
                                  "&:hover": { bgcolor: ADMIN_THEME.primaryHover },
                                }
                              : {
                                  borderColor: ADMIN_THEME.softBorder,
                                  "&:hover": { bgcolor: ADMIN_THEME.soft },
                                }
                          }
                        />
                      ))}
                    </Box>
                    <TextField
                      fullWidth
                      multiline
                      minRows={4}
                      value={question}
                      onChange={(event) => setQuestion(event.target.value)}
                      sx={{
                        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: ADMIN_THEME.primary,
                        },
                      }}
                    />
                    <Box display="flex" justifyContent="flex-end" mt={2}>
                      <Button
                        variant="contained"
                        onClick={handleAskMetrics}
                        disabled={asking || !status.configured}
                        startIcon={asking ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
                        sx={adminBtnSx}
                      >
                        {asking ? "Analyzing..." : "Analyze with Claude"}
                      </Button>
                    </Box>
                    {answer && (
                      <Box sx={{ mt: 2.5, p: 2, bgcolor: ADMIN_THEME.soft, border: `1px solid ${ADMIN_THEME.softBorder}`, borderRadius: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1, color: ADMIN_THEME.primaryHover }}>Claude</Typography>
                        <Typography variant="body1" component="div" sx={{ whiteSpace: "pre-wrap" }}>{answer}</Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </>
        )}
      </div>

      <Dialog
        open={confirmModalOpen}
        onClose={closeConfirmModal}
        fullWidth
        maxWidth="sm"
        aria-labelledby="claude-confirm-title"
      >
        <DialogTitle
          id="claude-confirm-title"
          sx={{
            pr: 6,
            fontWeight: "bold",
            color: ADMIN_THEME.primaryHover,
            borderBottom: `1px solid ${ADMIN_THEME.softBorder}`,
          }}
        >
          Confirm notification (Claude Ops)
          <IconButton
            aria-label="close"
            onClick={closeConfirmModal}
            disabled={confirming}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Confirm se pehle title / message / time edit kar sakte ho. MongoDB write isi final data pe hoga.
          </Typography>

          <Box
            sx={{
              mb: 2,
              p: 1.5,
              bgcolor: ADMIN_THEME.soft,
              border: `1px solid ${ADMIN_THEME.softBorder}`,
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Source:</strong> Claude Ops Chat
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Recipients:</strong>{" "}
              {(chatContext.pendingAction?.recipientCount || 0).toLocaleString()}
            </Typography>
            {chatContext.pendingAction?.audienceFilters ? (
              <Typography variant="caption" color="textSecondary" sx={{ display: "block" }}>
                Filters: {JSON.stringify(chatContext.pendingAction.audienceFilters)}
              </Typography>
            ) : null}
          </Box>

          <FormControl component="fieldset" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 0.5 }}>
              Action
            </Typography>
            <RadioGroup
              row
              value={confirmDraft.mode}
              onChange={(event) => {
                const mode = event.target.value;
                setConfirmDraft((prev) => ({ ...prev, mode }));
                setConfirmDraftError((prev) => ({ ...prev, scheduledAt: undefined }));
              }}
            >
              <FormControlLabel value="send_now" control={<Radio sx={{ color: ADMIN_THEME.primary, "&.Mui-checked": { color: ADMIN_THEME.primary } }} />} label="Send now" />
              <FormControlLabel value="schedule" control={<Radio sx={{ color: ADMIN_THEME.primary, "&.Mui-checked": { color: ADMIN_THEME.primary } }} />} label="Schedule" />
            </RadioGroup>
          </FormControl>

          <TextField
            fullWidth
            label="Title"
            value={confirmDraft.title}
            onChange={(event) => {
              setConfirmDraft((prev) => ({ ...prev, title: event.target.value }));
              if (confirmDraftError.title) setConfirmDraftError((prev) => ({ ...prev, title: undefined }));
            }}
            error={Boolean(confirmDraftError.title)}
            helperText={confirmDraftError.title || `${(confirmDraft.title || "").length}/120`}
            inputProps={{ maxLength: 120 }}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Message"
            value={confirmDraft.message}
            onChange={(event) => {
              setConfirmDraft((prev) => ({ ...prev, message: event.target.value }));
              if (confirmDraftError.message) setConfirmDraftError((prev) => ({ ...prev, message: undefined }));
            }}
            error={Boolean(confirmDraftError.message)}
            helperText={confirmDraftError.message || `${(confirmDraft.message || "").length}/500`}
            inputProps={{ maxLength: 500 }}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Title (Hindi) — optional"
            value={confirmDraft.titleHi}
            onChange={(event) => setConfirmDraft((prev) => ({ ...prev, titleHi: event.target.value }))}
            inputProps={{ maxLength: 120 }}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Message (Hindi) — optional"
            value={confirmDraft.messageHi}
            onChange={(event) => setConfirmDraft((prev) => ({ ...prev, messageHi: event.target.value }))}
            inputProps={{ maxLength: 500 }}
            sx={{ mb: 2 }}
          />

          {confirmDraft.mode === "schedule" ? (
            <TextField
              fullWidth
              type="datetime-local"
              label="Schedule time (IST)"
              value={confirmDraft.scheduledAt}
              onChange={(event) => {
                setConfirmDraft((prev) => ({ ...prev, scheduledAt: event.target.value }));
                if (confirmDraftError.scheduledAt) {
                  setConfirmDraftError((prev) => ({ ...prev, scheduledAt: undefined }));
                }
              }}
              error={Boolean(confirmDraftError.scheduledAt)}
              helperText={confirmDraftError.scheduledAt || "Asia/Kolkata wall-clock time"}
              InputLabelProps={{ shrink: true }}
            />
          ) : (
            <Typography variant="caption" color="textSecondary">
              Send now: confirm ke turant baad push queue start hogi.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            variant="outlined"
            onClick={closeConfirmModal}
            disabled={confirming}
            sx={adminOutlinedBtnSx}
          >
            Back
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmAction}
            disabled={confirming}
            startIcon={confirming ? <CircularProgress size={16} color="inherit" /> : null}
            sx={adminBtnSx}
          >
            {confirming ? "Writing..." : "Confirm Mongo write"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ClaudeAnalysis;
